import React from "react";
import { clsx } from "clsx";

import Headline from "~components/shared/headline/Headline";
import OptionalEyebrow, {
    type LiveBlogStatus,
    type PresetLabel,
    AUTHOR_EYEBROW,
} from "~components/shared/optional-eyebrow/OptionalEyebrow";
import Summary from "~components/shared/summary/Summary";
import { useLiveBlog } from "~hooks/useLiveBlog";
import { isOpinionItem } from "~models/transforms/eyebrow";
import { HEADLINE_SIZE } from "~types/lineup";
import { type CuratedItem, getMeta, isCuratedStory } from "~types/types";
import { isFeaturedStyle } from "~utils/isFeaturedStyle";
import { getOpinionBybrowPrefix } from "~utils/opinionBybrow";

import { getOpinionAuthorsText } from "../brow/Brow";
import PhoenixByline from "../phoenix-byline/PhoenixByline";

import styles from "./StoryBlock.module.css";

const Eyebrow = (props: {
    story: CuratedItem;
    isBylineEligible: boolean;
    isSecondaryStory?: boolean;
    labelDropdown?: PresetLabel;
    liveBlogState?: LiveBlogStatus;
    isAuthorEyebrow: boolean;
}) => {
    const {
        story,
        isSecondaryStory = false,
        isBylineEligible = true,
        labelDropdown,
        liveBlogState,
        isAuthorEyebrow,
    } = props;
    if (!isCuratedStory(story)) return null;

    const isOpinionStory = isOpinionItem(story);
    const opinionLabel =
        isSecondaryStory && !isBylineEligible ? "opinionRelated" : "opinion";
    const opinionAuthor = getOpinionAuthorsText(story.credits);
    const opinionRole = getOpinionBybrowPrefix(story.credits);

    const getPresetLabel = () => {
        // this is checking if a labelDropdown override has been passed into this component as props
        // if it has, we want to use it, as-is. The component is basically saying "Use this treatment".
        if (labelDropdown) {
            return labelDropdown;
        }

        if (isAuthorEyebrow) {
            return AUTHOR_EYEBROW;
        }

        if (isOpinionStory) {
            return opinionLabel;
        }

        // neither conditions is met, use the story's labelDropdown value.
        return story.labelDropdown;
    };

    return (
        <OptionalEyebrow
            liveBlogState={liveBlogState}
            label={story.label}
            franchise={story.franchise}
            authorName={isOpinionStory ? opinionAuthor : story.byline}
            authorRole={isOpinionStory ? opinionRole : ""}
            updatedAt={story.updatedAt}
            publishedAt={story.publishedAt}
            textLabel={story.textLabel}
            presetLabel={getPresetLabel()}
            nicodes={story.nicodes}
        />
    );
};

// TODO(@cmorrissey23): I created [ENG4AUD-914] to revisit how we handle
// setting headline sizes. I think this can more flexible, as it is
// now it forces us to prop-drill, add a new prop, or add CSS overrides.
const determineHeadlineSize = (
    isSecondaryStory: boolean,
    isExtraLargeHeadline: boolean,
    isExtraSmallHeadline: boolean,
    isFullZone: boolean,
    showVerticalVideoHeadline: boolean
) => {
    if (showVerticalVideoHeadline) {
        return HEADLINE_SIZE.MEDIUM;
    }

    if (isExtraLargeHeadline) {
        return HEADLINE_SIZE.EXTRA_LARGE;
    }

    if (isExtraSmallHeadline) {
        return HEADLINE_SIZE.EXTRA_SMALL;
    }

    if (isSecondaryStory) {
        return HEADLINE_SIZE.SMALL;
    }

    if (isFullZone) {
        return HEADLINE_SIZE.FULL_ZONE;
    }

    return HEADLINE_SIZE.LARGE;
};

interface StoryBlockProps {
    className?: string;
    isSecondaryStory?: boolean;
    name?: string;
    story: CuratedItem;
    withDek?: boolean;
    withOptionalEyebrow?: boolean;
    isBylineEligible?: boolean;
    forceShowByline?: boolean;
    shouldUseLink?: boolean;
    isExtraLargeHeadline?: boolean;
    isExtraSmallHeadline?: boolean;
    isFullZone?: boolean;
    labelDropdown?: PresetLabel;
    liveBlogState?: LiveBlogStatus;
    storyIndex: number;
    showVerticalVideoHeadline?: boolean;
    disableFeaturedStyling?: boolean;
    isAuthorEyebrow?: boolean;
}

export const StoryBlock = ({
    className,
    isSecondaryStory = false,
    story,
    withDek = false,
    withOptionalEyebrow = true,
    isBylineEligible = false,
    forceShowByline = false,
    shouldUseLink = true,
    isExtraLargeHeadline = false,
    isExtraSmallHeadline = false,
    isFullZone = false,
    labelDropdown,
    liveBlogState,
    storyIndex,
    showVerticalVideoHeadline = false,
    disableFeaturedStyling = false,
    isAuthorEyebrow = false,
}: StoryBlockProps) => {
    const isOpinionStory = isOpinionItem(story);
    const isLiveBlog = story.type === "liveblog";
    const { liveBlog } = useLiveBlog(
        !liveBlogState && isLiveBlog
            ? getMeta(story)?.terminalBlogId
            : undefined
    );
    const isComingUp =
        liveBlog?.state === "live" && liveBlog?.posts?.length === 1;

    let storyBlock = (
        <div
            className={clsx(className, styles.storyBlock, {
                [styles.secondaryStory]: isSecondaryStory,
            })}
            data-component="story-block"
        >
            {withOptionalEyebrow && (
                <Eyebrow
                    story={story}
                    liveBlogState={
                        isComingUp
                            ? "comingUp"
                            : liveBlog?.state || liveBlogState
                    }
                    isSecondaryStory={isSecondaryStory}
                    isBylineEligible={isBylineEligible}
                    labelDropdown={labelDropdown}
                    isAuthorEyebrow={isAuthorEyebrow}
                />
            )}
            <Headline
                text={story.headline}
                isPhoenix
                isFeaturedStyle={
                    !disableFeaturedStyling &&
                    !isOpinionStory &&
                    isCuratedStory(story) &&
                    isFeaturedStyle(
                        story.variation,
                        story.type,
                        story.brand,
                        story.franchise
                    )
                }
                size={determineHeadlineSize(
                    isSecondaryStory,
                    isExtraLargeHeadline,
                    isExtraSmallHeadline,
                    isFullZone,
                    showVerticalVideoHeadline
                )}
                className={styles.storyBlockHeadline}
            />
            {withDek && (
                <Summary
                    className={styles.storyBlockSummary}
                    summary={story.summary}
                    isPhoenix
                />
            )}
            {story && isBylineEligible && (
                <PhoenixByline
                    forceShowByline={forceShowByline}
                    story={story}
                />
            )}
        </div>
    );

    if (story.url && shouldUseLink) {
        storyBlock = (
            <a
                className={styles.storyLink}
                href={story.url}
                data-link-index={storyIndex}
                data-component="story-link"
            >
                {storyBlock}
            </a>
        );
    }

    return storyBlock;
};
